<script setup lang="ts">
import { ref, watch } from 'vue';
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import BadgeLegalStep from '@/components/ui/badges/BadgeLegalStep.vue';
import BooleanBadge from '@/components/ui/badges/BooleanBadge.vue';
import { claimLegalStepByName, legalSteps } from '@/utils/config/constants/claims-legal-steps';

const props = defineProps<{
  attribute: string;
  attributeName: string;
  size?: "md" | "lg"
}>()

interface AisRefinements {
  items: any;
  isShowingMore: any;
  isFromSearch: any;
  canToggleShowMore: any;
  refine: any;
  createURL: any;
  toggleShowMore: any;
  searchForItems: any;
  sendEvent: any;
}

const selected = ref([]);

const isLegalStep = props.attribute === 'claimLegalStepName'

// Function to sort items
function sortItems(items: any[]) {
  if (isLegalStep) {
    items.sort((a, b) => claimLegalStepByName(a.value).id - claimLegalStepByName(b.value).id);
  }
  return items;
}

const transformItem = (value: any) => value === "true" ? "Oui" : (value === "false" ? "Non" : value);
const isBoolean = (value: any) => value === "true" || value === "false";
</script>
<template>
  <div class="text-left">
    <RadioGroup v-model="selected">
    <RadioGroupLabel class="text-base font-semibold leading-6 text-gray-900">{{ attributeName }}</RadioGroupLabel>
      <ais-refinement-list :attribute="attribute" operator="or" :transform-items="sortItems" 
        :class="{
          'grid grid-cols-1 mt-4 gap-y-6 sm:grid-cols-6 sm:gap-x-4': isLegalStep,
          'grid grid-cols-2 mt-4 gap-y-6 sm:gap-x-4': !isLegalStep,
        }"
      >
        <template v-slot="{ items, isFromSearch, refine }">
          <RadioGroupOption as="template" v-for="(item, index) in items" :key="index" :value="item" v-slot="{ active, checked }">
            <div  @click.prevent="refine(item.value)"
              :class="[item.isRefined ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-gray-300', 'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none']">
              <span class="flex flex-1">
                <span class="flex flex-col">
                  <RadioGroupLabel as="span" class="block text-sm font-medium text-gray-900">
                    <BadgeLegalStep v-if="isLegalStep" :name="item.value" :dot="false" />
                    <p v-else :class="{ 'font-bold': item.isRefined }">
                      <span v-if="isBoolean(item.value)"><BooleanBadge :value="item.value === 'true' ? true : false" /></span>
                      <span v-else>{{ item.value }}</span>
                    </p>
                    <!-- {{ legalSteps[item.value].displayText }} -->
                  </RadioGroupLabel>
                  <RadioGroupDescription as="span" class="flex items-center mt-1 text-sm text-gray-500">{{ item.count }}</RadioGroupDescription>
                  <!-- <RadioGroupDescription as="span" class="mt-6 text-sm font-medium text-gray-900">{{ item }}</RadioGroupDescription> -->
                </span>
              </span>
              <CheckCircleIcon :class="[!item.isRefined ? 'invisible' : '', 'h-5 w-5 text-indigo-600']" aria-hidden="true" />
              <span :class="[item.isRefined ? 'border-indigo-600' : 'border-transparent', 'pointer-events-none absolute -inset-px rounded-lg']" aria-hidden="true" />
            </div>
          </RadioGroupOption>




          <!-- <Combobox v-model="selected" v-slot="{ open, disabled, activeIndex, activeOption }" multiple>
            <ComboboxLabel class="block text-sm font-medium text-gray-700">{{ attributeName }}</ComboboxLabel>
            <div class="relative mt-1">
              <ComboboxButton class="w-full">
                <ComboboxInput type="search"
                  @keyup.enter="refinements.refine(activeOption || '')"
                  class="py-2 pl-3 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  :class="size === 'lg' ? 'w-72' : 'w-56'"
                  :display-value="() => query" :placeholder="refinements.items.some(item => item.isRefined)
                    ? refinements.items.filter(item => item.isRefined).map(item => item.value)
                    : `${attributeName}...`" @change="search(($event.currentTarget as any).value, refinements)" />
                <kbd
                  class="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 rounded-r-md focus:outline-none">
                  <ChevronUpDownIcon class="w-5 h-5" aria-hidden="true" />
                </kbd>
              </ComboboxButton>

              <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <ComboboxOptions
                  class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  :key="refinements.length">
                  <div v-if="refinements.isFromSearch && !refinements.items.length"
                    class="relative px-4 py-2 text-gray-700 cursor-default select-none">
                    Pas de résultat.
                  </div>

                  <ComboboxOption v-for="item in refinements.items"
                    as="template"
                    :key="item.value"
                    :value="item.value"
                    v-slot="{ selected, active }"
                  >
                    <li v-if="item.value" class="relative py-2 pl-10 pr-4 cursor-default select-none" :class="{
                      'bg-blue-600 text-white': active,
                      'text-gray-900': !active,
                    }">
                      <a
                        :href="refinements.createURL(item)"
                        @click.prevent="refinements.refine(item.value)"
                        class="block truncate" :style="{ fontWeight: item.isRefined ? 'bold' : 'normal' }"
                        :class="{ 'font-medium': selected, 'font-normal': !selected }">
                        <ais-highlight attribute="item" :hit="item" />
                        <span class="text-sm" :class="active ? 'text-blue-100' : 'text-gray-400'">
                          ({{ item.count }})
                        </span>
                      </a>

                      <span v-if="item.isRefined || selected" class="absolute inset-y-0 left-0 flex items-center pl-3"
                        :class="{ 'text-white': selected, 'text-blue-600': !selected }">
                        <CheckIcon class="w-5 h-5" aria-hidden="true" />
                      </span>
                    </li>
                  </ComboboxOption>
                </ComboboxOptions>
              </TransitionRoot>
            </div>
          </Combobox> -->
        </template>
      </ais-refinement-list>

  </RadioGroup>
</div>
</template>
<style>
.ais-Highlight-highlighted {
  @apply bg-transparent !important;
  @apply font-bold !important;
  @apply text-inherit !important;
}
</style>